import React, { useEffect, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import CreateNewIdentity from "./CreateNewIdentity/CreateNewIdentity";
import useNotification from "../../hooks/useNotification";
import Search from "./Search/Search";
import { FormattedMessage, useIntl } from "react-intl";
import Tooltip from "../Tooltip/Tooltip";
import DateToolTip from "../DateToolTip/DateToolTip";
import useHover from "../../hooks/useHover";
import useTracker from "../../hooks/useTracker";
import { isActionRestricted } from "../../features/configSlice";
import { ActionCreateIdentity } from "../../utils/controls";
import SearchResultMessage from "./Search/SearchResultMessage";
import StaticTableWithPagination from "../StaticTableWithPagination/StaticTableWithPagination";
import { fetchDigitalIdentities } from "../../features/digitalIdentitiesSlice";
const IdentityManagement = ({ baseLoading }) => {
  const { scope: customerScope } = useSelector((state) => state.controller);
  const { controller: controllerCodeValue } = useSelector(
    (state) => state.controllers
  );

  const intl = useIntl();
  const { errorToast } = useNotification();
  const dispatch = useDispatch();
  const state = useSelector((state) => state.config);
  const {
    digitalIdentities,
    page,
    search,
    morePages,
    status: idStatus,
  } = useSelector((state) => state.digitalIdentities);
  const [loading, setLoader] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isUnsavedOpen, setIsUnsavedOpen] = useState(false);
  // getting searchFilterSelect value from  local storage if present othewise it will empty
  const trackerDetails = useSelector((state) => state?.TrackerReducer);
  const [filter, setFilter] = useState({
    type: trackerDetails?.identity_search.type || "",
    value: trackerDetails?.identity_search.value || "",
    done: trackerDetails?.identity_search.done || 0,
  });

  const { handleMouseEnter, handleMouseLeave, hoveredData, ishovered } =
    useHover();
  const { identitySearchTracker } = useTracker();
  const getConsentTableData = async (page = 1) => {
    try {
      dispatch(
        fetchDigitalIdentities({
          code: controllerCodeValue,
          scope: customerScope,
          debug: false,
          page: page,
        })
      );
    } catch (error) {}
  };

  const handleCreateModalOpen = (state, refresh) => {
    setIsOpen(state);
    if (refresh) getConsentTableData();
  };

  useEffect(() => {
    if (
      !baseLoading &&
      !(
        trackerDetails?.identity_search.type == filter?.type &&
        trackerDetails?.identity_search.value == filter?.value
      )
    )
      setFilter({
        type: trackerDetails?.identity_search.type || "veeva",
        value: trackerDetails?.identity_search.value || "",
      });
    else if (
      !baseLoading &&
      trackerDetails &&
      trackerDetails?.form_type == "create" &&
      trackerDetails?.modal_type == "create"
    )
      setIsOpen(true);
  }, [trackerDetails]);

  //#region handling page loading
  useEffect(() => {
    const idsLoaded = ["succeeded", "failed"]?.includes(idStatus);
    if (!baseLoading && idsLoaded) setLoader(false);
    else setLoader(true);
  }, [baseLoading, idStatus]);
  //#endregion

  const columns = [
    {
      header: intl.formatMessage({ id: "label.type", defaultMessage: "Type" }),
      accessorKey: "type",
    },
    {
      header: intl.formatMessage({
        id: "label.value",
        defaultMessage: "Value",
      }),
      accessorKey: "value",
    },
    {
      header: intl.formatMessage({ id: "label.name", defaultMessage: "Name" }),
      accessorKey: "first_name",
      accessorFn: (row) => `${row?.first_name || ""} ${row?.last_name || ""}`,
    },
    {
      header: () => {
        return (
          <Tooltip position="right" text={"Date format: DD.MM.YYYY"}>
            <span className="pe-1">
              {intl.formatMessage({
                id: "label.last-sync-date",
                defaultMessage: "Last Sync Date",
              })}
            </span>
          </Tooltip>
        );
      },

      accessorKey: "integration_update_datetime",
      sortType: (rowA, rowB) => {
        return (
          new Date(rowA.original.integration_update_datetime) -
          new Date(rowB.original.integration_update_datetime)
        );
      },
      cell: (info) => {
        return (
          <div
            onMouseEnter={() => handleMouseEnter(info.getValue())}
            onMouseLeave={handleMouseLeave}
            className="d-flex align-items-center"
          >
            <span className="pe-1">
              {moment.utc(info.getValue()).local().format("DD.MM.YYYY HH:mm")}
            </span>
            <DateToolTip ishovered={ishovered} hoveredData={hoveredData} />
          </div>
        );
      },
    },
  ];

  // #region : Pagination controls
  const handleFirstPageChange = () => {
    if (page !== 1) getConsentTableData(1);
  };
  const handlePrevPageChange = () => {
    if (page !== 0) {
      const prev = page - 1;
      getConsentTableData(prev);
    }
  };
  const handleNextPageChange = () => {
    if (morePages) {
      const next = page + 1;
      getConsentTableData(next);
    }
  };
  // #endregion

  const openModal = () => setIsOpen(true);

  // #region Search functionalities here
  const handleFilterChange = (obj) => {
    identitySearchTracker({ ...obj, done: 0 });
    setFilter(obj);
  };
  const handleSearch = async (obj) => {
    if (!obj?.value) getConsentTableData(1);
    else if (obj?.value?.length <= 3)
      errorToast(
        intl.formatMessage({
          id: "id.search.3-letter-warning",
          defaultMessage: "Please Enter More than 3 Characters",
        })
      );
    else {
      try {
        dispatch(
          fetchDigitalIdentities({
            code: controllerCodeValue,
            scope: customerScope,
            type: obj?.type,
            value: obj?.value,
          })
        );
        identitySearchTracker({
          type: obj?.type,
          value: obj?.value,
          done: 1,
        });
      } catch (error) {}
    }
  };
  // #endregion

  // #region Initial Loading

  useEffect(() => {
    if (!baseLoading && controllerCodeValue && customerScope) {
      //#region //tracker with search data=>call search call
      if (
        trackerDetails?.identity_search?.type &&
        trackerDetails?.identity_search?.value &&
        trackerDetails?.identity_search?.done
      ) {
        dispatch(
          fetchDigitalIdentities({
            code: controllerCodeValue,
            scope: customerScope,
            type: trackerDetails?.identity_search?.type,
            value: trackerDetails?.identity_search?.value,
          })
        );
      }
      //#endregion
      else {
        // Tracker with controller and scope => fetch the table data
        getConsentTableData();
        //tracker with create form data
        // 1. open the create new modal and fill values
      }
    }
  }, [baseLoading]);

  // #endregion

  return (
    <>
      <div className="identity-management">
        <h2 className="fs-3 fw-normal py-2">
          <FormattedMessage
            id="id.title"
            defaultMessage="Identity Management"
          />
        </h2>
        <div className=" search-bar-container">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <Search
              filter={filter}
              handleChange={handleFilterChange}
              handleSearch={handleSearch}
            />
            {!isActionRestricted(state, ActionCreateIdentity) && (
              <button
                className="btn-primary"
                style={{ padding: "12px 17px 12px 17px" }}
                onClick={openModal}
              >
                +{" "}
                <FormattedMessage
                  id="id.createNew"
                  defaultMessage="CREATE NEW"
                />
              </button>
            )}
          </div>
          {search?.type && search?.value && !loading && (
            <SearchResultMessage searchFilter={search?.value} />
          )}
        </div>
        <StaticTableWithPagination
          data={digitalIdentities}
          columns={columns}
          loading={loading}
          page={page}
          currentPage={page}
          morePage={morePages}
          handleFirstPageChange={handleFirstPageChange}
          handlePrevPageChange={handlePrevPageChange}
          handleNextPageChange={handleNextPageChange}
          removePagination={search?.value}
        />
      </div>
      {isOpen && (
        <CreateNewIdentity
          isOpen={isOpen}
          setIsOpen={handleCreateModalOpen}
          isUnsavedOpen={isUnsavedOpen}
          setIsUnsavedOpen={setIsUnsavedOpen}
        />
      )}
    </>
  );
};

export default IdentityManagement;
